import { Trophy, Medal, Award, TrendingUp, TrendingDown, Minus, Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { leaderboardAPI } from '@/api/leaderboard';
import { usePoints } from '@/context/PointsContext';
import { getUserData, getPortalType } from '@/utils/auth';

export default function LeaderboardPage() {
  const { points } = usePoints();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gradeId, setGradeId] = useState(null);
  const [activePeriod, setActivePeriod] = useState('all_time'); // all_time, weekly, monthly

  // Get grade ID from user data (stored in localStorage)
  useEffect(() => {
    try {
      const portal = getPortalType(window.location.pathname || '');
      const userData = getUserData(portal);
      
      if (userData?.grade?.id) {
        setGradeId(userData.grade.id);
      } else {
        // Check localStorage for student_grade_id (set during login)
        const storedGradeId = localStorage.getItem('student_grade_id');
        if (storedGradeId) {
          setGradeId(parseInt(storedGradeId));
        } else {
          setError('Unable to load leaderboard. Grade information not found. Please try logging in again.');
          setLoading(false);
        }
      }
    } catch (err) {
      console.error('Error getting grade ID:', err);
      setError('Unable to load leaderboard. Please refresh the page.');
      setLoading(false);
    }
  }, []);

  // Fetch leaderboard data when gradeId or activePeriod changes
  useEffect(() => {
    if (!gradeId) return;

    const fetchLeaderboard = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch both leaderboard and user's rank with the selected period
        const [leaderboardResponse, rankResponse] = await Promise.all([
          leaderboardAPI.getLeaderboard(gradeId, activePeriod),
          leaderboardAPI.getMyRank(gradeId, activePeriod).catch(() => null), // Optional, don't fail if it errors
        ]);

        setLeaderboardData(leaderboardResponse.leaderboard || []);
        
        if (rankResponse) {
          setMyRank(rankResponse);
        }
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
        setError(err.message || 'Failed to load leaderboard. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [gradeId, activePeriod]);

  const getBadge = (rank) => {
    if (rank === 1) return '👑'; // Crown for class topper
    return '⭐';
  };

  const getPeriodDisplayName = (period) => {
    switch (period) {
      case 'all_time': return 'All Time';
      case 'weekly': return 'Weekly';
      case 'monthly': return 'Monthly';
      default: return 'All Time';
    }
  };

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] p-3 sm:p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="text-center lg:text-left">
          <div className="flex items-center justify-center lg:justify-start gap-3 sm:gap-4">
            <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-[hsl(var(--green-bright))]" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">Leaderboard</h1>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Compete with other learners and climb the ranks</p>
        </div>

        {/* Period Tabs */}
        <div className="grid grid-cols-3 border-b border-[hsl(var(--card-border))] gap-0">
          <Button
            variant="ghost"
            onClick={() => setActivePeriod('all_time')}
            className={`px-2 sm:px-4 py-2 sm:py-3 rounded-t-lg w-full text-xs sm:text-sm ${
              activePeriod === 'all_time'
                ? 'bg-[hsl(var(--card-bg))] border-b-2 border-[hsl(var(--green-bright))] text-[hsl(var(--green-bright))] font-semibold'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            All Time
          </Button>
          <Button
            variant="ghost"
            onClick={() => setActivePeriod('weekly')}
            className={`px-2 sm:px-4 py-2 sm:py-3 rounded-t-lg w-full text-xs sm:text-sm ${
              activePeriod === 'weekly'
                ? 'bg-[hsl(var(--card-bg))] border-b-2 border-[hsl(var(--green-bright))] text-[hsl(var(--green-bright))] font-semibold'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Weekly
          </Button>
          <Button
            variant="ghost"
            onClick={() => setActivePeriod('monthly')}
            className={`px-2 sm:px-4 py-2 sm:py-3 rounded-t-lg w-full text-xs sm:text-sm ${
              activePeriod === 'monthly'
                ? 'bg-[hsl(var(--card-bg))] border-b-2 border-[hsl(var(--green-bright))] text-[hsl(var(--green-bright))] font-semibold'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Monthly
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
          <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
            <CardContent className="p-2 sm:p-4 lg:p-6">
              <div className="flex flex-col items-center text-center gap-2">
                <Medal className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />
                <p className="text-xs sm:text-sm text-muted-foreground">Your Rank</p>
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-foreground" />
                ) : (
                  <p className="text-lg sm:text-2xl font-bold text-foreground">
                    {myRank ? `#${myRank.rank}` : 'N/A'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
            <CardContent className="p-2 sm:p-4 lg:p-6">
              <div className="flex flex-col items-center text-center gap-2">
                <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {activePeriod === 'all_time' ? 'Your XP' : `Your ${getPeriodDisplayName(activePeriod)} XP`}
                </p>
                <p className="text-lg sm:text-2xl font-bold text-foreground">
                  {myRank?.period_xp?.toLocaleString() || (activePeriod === 'all_time' ? (points?.total_xp?.toLocaleString() || '0') : '0')}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
            <CardContent className="p-2 sm:p-4 lg:p-6">
              <div className="flex flex-col items-center text-center gap-2">
                <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
                <p className="text-xs sm:text-sm text-muted-foreground">Total Students</p>
                <p className="text-lg sm:text-2xl font-bold text-foreground">
                  {myRank?.total_students || '-'}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
            <CardContent className="p-2 sm:p-4 lg:p-6">
              <div className="flex flex-col items-center text-center gap-2">
                {myRank?.rank_change !== null && myRank?.rank_change !== undefined ? (
                  myRank.rank_change > 0 ? (
                    <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
                  ) : myRank.rank_change < 0 ? (
                    <TrendingDown className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
                  ) : (
                    <Minus className="w-6 h-6 sm:w-8 sm:h-8 text-gray-500" />
                  )
                ) : (
                  <Minus className="w-6 h-6 sm:w-8 sm:h-8 text-gray-500" />
                )}
                <p className="text-xs sm:text-sm text-muted-foreground">Rank Change</p>
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-foreground" />
                ) : (
                  <div className="flex items-center gap-2">
                    {myRank?.rank_change !== null && myRank?.rank_change !== undefined ? (
                      <>
                        {myRank.rank_change > 0 ? (
                          <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                        ) : myRank.rank_change < 0 ? (
                          <ArrowDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                        ) : (
                          <Minus className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                        )}
                        <p className="text-lg sm:text-2xl font-bold text-foreground">
                          {myRank.rank_change > 0 ? `+${myRank.rank_change}` : myRank.rank_change}
                        </p>
                      </>
                    ) : (
                      <p className="text-base sm:text-xl font-bold text-muted-foreground">-</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Error Message */}
        {error && (
          <Card className="bg-red-500/10 border-red-500/20">
            <CardContent className="p-4">
              <p className="text-red-400">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Leaderboard Table */}
        <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <CardHeader>
            <CardTitle className="text-foreground">Top Performers</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-foreground" />
              </div>
            ) : leaderboardData.length > 0 ? (
              <div className="space-y-3">
                {leaderboardData.map((entry) => (
                  <div
                    key={entry.rank}
                    className="flex items-center justify-between p-3 sm:p-4 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg hover:border-[hsl(var(--primary))] transition-all"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="text-xl sm:text-2xl">{getBadge(entry.rank)}</div>
                      <div>
                        <p className="text-sm sm:text-base font-semibold text-foreground">{entry.full_name || entry.username}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {(entry.period_xp !== undefined ? entry.period_xp : entry.total_xp)?.toLocaleString() || 0} XP
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-xs sm:text-sm text-muted-foreground">
                <p>No leaderboard data available yet. Start learning to climb the ranks! 🚀</p>
              </div>
            )}
            <div className="mt-6 text-center text-xs sm:text-sm text-muted-foreground">
              <p>Keep learning to climb the leaderboard! 🚀</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

